import React, { useState, useEffect } from 'react';
import { MdEdit, MdDelete } from 'react-icons/md';
import { FiPlus, FiCopy, FiCheck } from 'react-icons/fi';
import { BiSolidGift } from 'react-icons/bi';
import Layout from '../components/Layout';
import Toast, { ToastType } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import { apiService } from '../services/api';
import { PromoCode } from '../types';

const PromoCodes: React.FC = () => {
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    is_active: true,
    max_uses: '',
    expires_at: '',
  });
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const fetchPromoCodes = async () => {
    setLoading(true);
    try {
      const response = await apiService.getPromoCodes();
      if (response.success) {
        setPromoCodes(response.data);
      }
    } catch (error: any) {
      setToast({ message: error.message || 'Failed to fetch promo codes', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const data = {
      code: formData.code.trim(),
      description: formData.description.trim(),
      is_active: formData.is_active,
      max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
      expires_at: formData.expires_at || null,
    };

    try {
      if (editingId) {
        await apiService.updatePromoCode(editingId, data);
        setToast({ message: 'Promo code updated successfully', type: 'success' });
      } else {
        await apiService.createPromoCode(data);
        setToast({ message: 'Promo code created successfully', type: 'success' });
      }
      
      fetchPromoCodes();
      resetForm();
    } catch (error: any) {
      setToast({ message: error.message || 'Failed to save promo code', type: 'error' });
    }
  };

  const handleEdit = (promoCode: PromoCode) => {
    setEditingId(promoCode.id);
    setFormData({
      code: promoCode.code,
      description: promoCode.description,
      is_active: promoCode.is_active,
      max_uses: promoCode.max_uses ? promoCode.max_uses.toString() : '',
      expires_at: promoCode.expires_at ? promoCode.expires_at.split('T')[0] : '',
    });
    setShowForm(true);
  };

  const handleDelete = (promoCode: PromoCode) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Promo Code',
      message: `Are you sure you want to delete "${promoCode.code}"? ${
        promoCode.user_count > 0
          ? `This code has been used by ${promoCode.user_count} user(s) and cannot be deleted.`
          : 'This action cannot be undone.'
      }`,
      onConfirm: async () => {
        try {
          await apiService.deletePromoCode(promoCode.id);
          setToast({ message: 'Promo code deleted successfully', type: 'success' });
          fetchPromoCodes();
        } catch (error: any) {
          setToast({ message: error.message || 'Failed to delete promo code', type: 'error' });
        }
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      },
    });
  };

  const handleCopyLink = (code: string) => {
    let signupBaseUrl = '';
    const currentHost = window.location.host;
    
    if (currentHost.includes('localhost') || currentHost.includes('127.0.0.1')) {
      signupBaseUrl = `${window.location.protocol}//localhost:3000`;
    } else if (currentHost.includes('admin.')) {
      signupBaseUrl = `${window.location.protocol}//${currentHost.replace('admin.', 'app.')}`;
    } else if (currentHost.includes('panel.')) {
      signupBaseUrl = `${window.location.protocol}//${currentHost.replace('panel.', 'app.')}`;
    } else {
      signupBaseUrl = `${window.location.protocol}//${currentHost}`;
    }
    
    const link = `${signupBaseUrl}/auth/sign-up?promoCode=${code}`;
    navigator.clipboard.writeText(link);
    setCopiedCode(code);
    setToast({ message: 'Signup link copied to clipboard!', type: 'success' });
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const resetForm = () => {
    setFormData({
      code: '',
      description: '',
      is_active: true,
      max_uses: '',
      expires_at: '',
    });
    setEditingId(null);
    setShowForm(false);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No Expiry';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Layout>
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <BiSolidGift className="h-8 w-8 text-brand-500" />
          <h1 className="text-3xl font-bold text-navy-800 dark:text-white">Promo Codes</h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          Manage promotional codes for user sign-ups
        </p>
      </div>

      {/* Add Button */}
      {!showForm && (
        <div className="mb-6">
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 font-medium text-white shadow-lg transition hover:bg-brand-600"
          >
            <FiPlus className="h-5 w-5" />
            Add Promo Code
          </button>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-md dark:border-navy-700 dark:bg-navy-800">
          <h3 className="mb-4 text-lg font-semibold text-navy-800 dark:text-white">
            {editingId ? 'Edit Promo Code' : 'New Promo Code'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-navy-800 dark:text-white">
                  Code *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                  required
                  placeholder="e.g., TEAMTHER2026"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-navy-800 dark:text-white">
                  Max Uses
                </label>
                <input
                  type="number"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                  placeholder="Leave empty for unlimited"
                  min="1"
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-navy-800 dark:text-white">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                rows={2}
                placeholder="Internal description (optional)"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-navy-800 dark:text-white">
                  Expires At
                </label>
                <input
                  type="date"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                />
              </div>
              <div className="flex items-center">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                  />
                  <span className="text-sm font-medium text-navy-800 dark:text-white">
                    Active
                  </span>
                </label>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                className="rounded-lg bg-brand-500 px-6 py-2 font-medium text-white shadow-lg transition hover:bg-brand-600"
              >
                {editingId ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="rounded-lg border border-gray-300 px-6 py-2 font-medium text-gray-700 transition hover:bg-gray-100 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-md dark:border-navy-700 dark:bg-navy-800">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-navy-700">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">
                  Promo Code
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">
                  Description
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700 dark:text-gray-200">
                  End Date
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700 dark:text-gray-200">
                  Max Users
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700 dark:text-gray-200">
                  Users Count
                </th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700 dark:text-gray-200">
                  Status
                </th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-navy-700">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center">
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
                  </td>
                </tr>
              ) : promoCodes.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-500 dark:text-gray-400">
                    No promo codes found. Click "Add Promo Code" to create one.
                  </td>
                </tr>
              ) : (
                promoCodes.map((promoCode) => (
                  <tr
                    key={promoCode.id}
                    className="transition hover:bg-gray-50 dark:hover:bg-navy-700/50"
                  >
                    <td className="px-6 py-4">
                      <span className="font-mono font-semibold text-brand-500">
                        {promoCode.code}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                      {promoCode.description || '-'}
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600 dark:text-gray-400">
                      {formatDate(promoCode.expires_at)}
                    </td>
                    <td className="px-6 py-4 text-center text-sm font-medium text-navy-800 dark:text-white">
                      {promoCode.max_uses || 'Unlimited'}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center gap-1 rounded-full bg-brand-100 px-3 py-1 text-sm font-semibold text-brand-700 dark:bg-brand-900/30 dark:text-brand-400">
                        <BiSolidGift className="h-4 w-4" />
                        {promoCode.user_count || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span
                        className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                          promoCode.is_active
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400'
                        }`}
                      >
                        {promoCode.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleCopyLink(promoCode.code)}
                          className="rounded-lg p-2 text-blue-600 transition hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                          title="Copy signup link"
                        >
                          {copiedCode === promoCode.code ? (
                            <FiCheck className="h-5 w-5 text-green-600" />
                          ) : (
                            <FiCopy className="h-5 w-5" />
                          )}
                        </button>
                        <button
                          onClick={() => handleEdit(promoCode)}
                          className="rounded-lg p-2 text-orange-600 transition hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-900/20"
                          title="Edit"
                        >
                          <MdEdit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(promoCode)}
                          className="rounded-lg p-2 text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-900/20"
                          title={promoCode.user_count > 0 ? 'Cannot delete - code has been used' : 'Delete'}
                          disabled={promoCode.user_count > 0}
                        >
                          <MdDelete className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        type="danger"
      />
    </Layout>
  );
};

export default PromoCodes;
